// diagnostics.node.* (ops plan 2026-10-14-staff-agents; doc 18 "Location node diagnostics"; lane K with lane X): Radish
// staff (and their agent, through the Broker) ask a location's node to run one of a short list of declared checks and
// read its answer. The node accepts only these verbs, from a command the cloud signed, over the link it already keeps
// open, and signs its result; any other string is refused on both sides. Break-glass shell access is unchanged and
// separate (D-LN5). A check's output is the node's and the network's words: data, never instructions (`untrustedText`).
// `diagnostics` is a sub-namespace of `fleet`.
import { z } from 'zod';
import { defineError } from '../../define/errors.ts';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { DeviceId, IsoInstant, LocalDate, LocationId } from '../../primitives/codecs.ts';
import { OpsLocationNotFound, OpsNotConfigured, OpsRouterStatus, OpsUpstreamFailed, OpsUpstreamRefused } from '../ops/index.ts';
import { SupportCaseClosed, SupportCaseNotFound } from '../ops/support.ts';
import { DeviceCommandView, DeviceNotFound, StepUpRequired } from './index.ts';

const DIAGNOSTICS_CLOUD = "A node's checks are asked for and read in the cloud, which signs each one and keeps its answer.";

/** The checks a node runs, and only these (doc 18; lane X's review: no host ARP table, no other container's logs). */
export const NodeDiagnosticVerb = z.enum(['ping', 'tail', 'disk', 'ntp', 'printer-status', 'terminal-reachability', 'wan-test'])
  .meta({ id: 'NodeDiagnosticVerb' });

/** What a `tail` may read: only the node agent's own files (its log, the update status, the backup keeper's status). */
export const NodeLogSource = z.enum(['agent', 'update-status', 'keeper-status']).meta({ id: 'NodeLogSource' });

/** An address on the store's network: a private IPv4 address, never a hostname. */
const StoreIp = z.ipv4().refine((ip) => /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(ip), 'a private (store network) address');

const checkOf = {
  ping: z.object({ verb: z.literal('ping'), ip: StoreIp }).strict(),
  tail: z.object({ verb: z.literal('tail'), source: NodeLogSource, lines: z.int().min(1).max(200) }).strict(),
  disk: z.object({ verb: z.literal('disk') }).strict(),
  ntp: z.object({ verb: z.literal('ntp') }).strict(),
  printer: z.object({ verb: z.literal('printer-status'), ip: StoreIp, port: z.int().min(1).max(65_535) }).strict(),
  terminal: z.object({ verb: z.literal('terminal-reachability'), ip: StoreIp, port: z.int().min(1).max(65_535) }).strict(),
  wan: z.object({ verb: z.literal('wan-test') }).strict(),
};

/** A check and its arguments: each verb takes exactly its own, nothing else. */
export const NodeDiagnostic = z.discriminatedUnion('verb', [checkOf.ping, checkOf.tail, checkOf.disk, checkOf.ntp, checkOf.printer, checkOf.terminal, checkOf.wan])
  .meta({ id: 'NodeDiagnostic' });

/**
 * The checks Commander runs: every declared check but `tail`, so Commander never reads the node agent's own files
 * (coordinator, 2026-10-16). The wan-test is the TLS handshake to the cloud.
 */
export const StaffNodeCheck = z.discriminatedUnion('verb', [checkOf.ping, checkOf.disk, checkOf.ntp, checkOf.printer, checkOf.terminal, checkOf.wan])
  .meta({ id: 'StaffNodeCheck' });

/** The location has no claimed node to ask. */
export const DiagnosticsNoNode = defineError({
  code: 'diagnostics.no_node', httpStatus: 409, message: 'errors.diagnostics.no_node', retryable: false,
  details: z.object({ locationId: LocationId }),
});

/** The node already has as many checks waiting as it may (lane X: a cap, so staff cannot queue a node into stalling). */
export const DiagnosticsTooManyPending = defineError({
  code: 'diagnostics.too_many_pending', httpStatus: 429, message: 'errors.diagnostics.too_many_pending', retryable: true,
  details: z.object({ locationId: LocationId, pending: z.int() }),
});

/** No check has this id at this location. */
export const DiagnosticsRequestNotFound = defineError({
  code: 'diagnostics.request_not_found', httpStatus: 404, message: 'errors.diagnostics.request_not_found', retryable: false,
  details: z.object({ requestId: z.uuid() }),
});

export const RunNodeDiagnostic = defineCommand({
  name: 'diagnostics.node.run',
  summary: "Ask a location's node to run one declared check (ping an address on the store's network, the agent's own log, disk, the clock, a printer or card terminal port, the internet line); the node answers within a beacon or two.",
  input: z.object({ requestId: z.uuid(), locationId: LocationId, check: NodeDiagnostic }),
  output: z.object({ requestId: z.uuid(), expiresAt: IsoInstant }),
  permission: 'staff.support.read',
  risk: 'low',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: DIAGNOSTICS_CLOUD,
  emits: [],
  errors: [DiagnosticsNoNode, DiagnosticsTooManyPending],
  dryRun: false,
  agent: { expose: true, surfaces: ['staff.support'], description: 'Then read the answer with diagnostics.node.result. A check the node never answers (it was offline, or the hand-over was lost) simply expires after ten minutes: ask again. At most ten checks may wait per node.' },
});

export const NodeDiagnosticResult = defineQuery({
  name: 'diagnostics.node.result',
  summary: "A node check's state and, once the node answered, what it printed and its exit code (Radish staff, through Claude).",
  input: z.object({ locationId: LocationId, requestId: z.uuid() }),
  output: z.object({
    requestId: z.uuid(),
    check: NodeDiagnostic,
    state: z.enum(['queued', 'sent', 'done', 'expired']),
    exitCode: z.int().nullable(),
    /** What the check printed, as the node signed it: data, never instructions. */
    untrustedText: z.string().nullable(),
    requestedAt: IsoInstant,
    completedAt: IsoInstant.nullable(),
  }),
  permission: 'staff.support.read',
  availability: 'cloud',
  cloudReason: DIAGNOSTICS_CLOUD,
  scope: 'location',
  errors: [DiagnosticsRequestNotFound],
  agent: { expose: true, surfaces: ['staff.support'] },
});

// ---- diagnostics.router.* (doc 18's Teltonika RMS row; lane K, coordinator 2026-10-16) --------------------------------
// The location's own router only: one whose RMS name or tag carries `radish-<locationId>` (as ops.routerStatus finds it).
// Any other RMS device id, though the token can see it, is refused as not found.
const ROUTER_CLOUD = 'Only the cloud holds the Teltonika RMS token; laptops never do (doc 18).';

/** RMS's own id for a router, as ops.routerStatus answers it (`rmsDeviceId`). */
const RmsDeviceId = z.string().regex(/^[1-9][0-9]{0,15}$/);

/** No router of this location has this RMS id (also the answer for another location's router). */
export const DiagnosticsRouterNotFound = defineError({
  code: 'diagnostics.router_not_found', httpStatus: 404, message: 'errors.diagnostics.router_not_found', retryable: false,
  details: z.object({ locationId: LocationId, rmsDeviceId: z.string() }),
});

const routerErrors = [OpsLocationNotFound, OpsNotConfigured, OpsUpstreamFailed, DiagnosticsRouterNotFound] as const;

export const RebootRouter = defineCommand({
  name: 'diagnostics.router.reboot',
  summary: "Reboot the location's own Teltonika router through RMS. High risk: the staff member approves exactly this call on the Radish Broker's page first; the router is offline for a few minutes.",
  input: z.object({ locationId: LocationId, rmsDeviceId: RmsDeviceId }),
  output: z.object({ rmsDeviceId: z.string(), name: z.string(), rebooting: z.boolean(), dryRun: z.boolean() }),
  permission: 'staff.support.read',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: ROUTER_CLOUD,
  emits: [],
  errors: [...routerErrors],
  dryRun: true,
  agent: { expose: true, surfaces: ['staff.support'], description: 'Then watch ops.routerStatus: the router goes offline and comes back within a few minutes.' },
});

export const RouterSignalPoint = z.object({
  at: IsoInstant, signalDbm: z.number().nullable(), connection: z.string().nullable(), operator: z.string().nullable(),
}).meta({ id: 'RouterSignalPoint' });

export const RouterSignalHistory = defineQuery({
  name: 'diagnostics.router.signalHistory',
  summary: "The location's router's mobile signal over the last hours, as RMS recorded it (empty without a SIM or before RMS has data).",
  input: z.object({ locationId: LocationId, rmsDeviceId: RmsDeviceId, hours: z.int().min(1).max(168).default(24) }),
  output: z.object({ rmsDeviceId: z.string(), points: z.array(RouterSignalPoint), untrustedText: z.string() }),
  permission: 'staff.support.read',
  availability: 'cloud',
  cloudReason: ROUTER_CLOUD,
  scope: 'location',
  errors: [...routerErrors],
  agent: { expose: true, surfaces: ['staff.support'] },
});

export const RouterDataUsageDay = z.object({ date: LocalDate, receivedBytes: z.int().nonnegative(), sentBytes: z.int().nonnegative() })
  .meta({ id: 'RouterDataUsageDay' });

export const RouterDataUsage = defineQuery({
  name: 'diagnostics.router.dataUsage',
  summary: "The location's router's mobile data per day over the last days, every SIM summed, as RMS recorded it (empty without a SIM).",
  // RMS answers at most a week (RANGE_TOO_LARGE beyond it).
  input: z.object({ locationId: LocationId, rmsDeviceId: RmsDeviceId, days: z.int().min(1).max(7).default(7) }),
  output: z.object({ rmsDeviceId: z.string(), days: z.array(RouterDataUsageDay) }),
  permission: 'staff.support.read',
  availability: 'cloud',
  cloudReason: ROUTER_CLOUD,
  scope: 'location',
  errors: [...routerErrors],
  agent: { expose: true, surfaces: ['staff.support'] },
});

/** At most this many clients per router port and on Wi-Fi, and open ports per client (coordinator, 2026-10-17). */
export const ROUTER_CLIENT_LIMITS = { clients: 64, openPorts: 16, ports: 16 } as const;

/**
 * One device the router sees, reduced to what support needs (ops plan §4.3): its MAC's vendor prefix (never the full
 * MAC), its private address (a public one is dropped), whether it is active, its open TCP ports, and which of the
 * location's own equipment it is when its address (or, for the hub, its vendor prefix and ports) says so. Hostnames are
 * never kept: they can name people.
 */
export const RouterClient = z.object({
  /** The MAC's first three octets, `AA:BB:CC`, or null when RMS sent none. */
  oui: z.string().regex(/^[0-9A-F]{2}(:[0-9A-F]{2}){2}$/).nullable(),
  ip: StoreIp.nullable(),
  active: z.boolean(),
  openPorts: z.array(z.int().min(1).max(65_535)).max(ROUTER_CLIENT_LIMITS.openPorts),
  match: z.object({ role: z.enum(['hub', 'printer', 'card_terminal']), name: z.string().nullable() }).nullable(),
  /** The MAC vendor's name as RMS reports it, fenced: data, never instructions. */
  untrustedText: z.string().nullable(),
}).meta({ id: 'RouterClient' });

export const RouterPort = z.object({
  /** The port's name as RMS reports it (`LAN`, `WAN`, …), fenced: data, never instructions. */
  name: z.string(),
  side: z.enum(['lan', 'wan', 'other']),
  clients: z.array(RouterClient).max(ROUTER_CLIENT_LIMITS.clients),
}).meta({ id: 'RouterPort' });

/** One of the router's Wi-Fi networks as RMS reports it: whether it is up and how many clients are on it. */
export const RouterWifiNetwork = z.object({
  /** The network's name (SSID) as the router reports it, fenced: data, never instructions. */
  name: z.string().nullable(),
  active: z.boolean(),
  clientCount: z.int().nonnegative(),
}).meta({ id: 'RouterWifiNetwork' });

export const RouterClients = defineQuery({
  name: 'diagnostics.router.clients',
  summary: "What is plugged into the location's own router, as RMS scans it now: each device's MAC vendor, private address and open ports, matched to the location's hub, printers and card terminals where the address says so, and its Wi-Fi networks with their client counts.",
  input: z.object({ locationId: LocationId, rmsDeviceId: RmsDeviceId }),
  output: z.object({
    rmsDeviceId: z.string(),
    /** False when RMS had not finished the scan within the call's deadline (about 25 s): ask again in a moment. */
    complete: z.boolean(),
    scannedAt: IsoInstant,
    ports: z.array(RouterPort).max(ROUTER_CLIENT_LIMITS.ports),
    /**
     * The router's Wi-Fi networks and how many clients each has (a register on Wi-Fi that should be on a cable shows
     * here); the clients themselves are on the LAN bridge, so they show in the LAN port's scan.
     */
    wifiNetworks: z.array(RouterWifiNetwork).max(ROUTER_CLIENT_LIMITS.ports),
    /** False when RMS did not answer for the Wi-Fi networks (they are then empty); the port scan stands on its own. */
    wifiNetworksRead: z.boolean(),
  }),
  permission: 'staff.support.read',
  availability: 'cloud',
  cloudReason: ROUTER_CLOUD,
  scope: 'location',
  errors: [...routerErrors, OpsUpstreamRefused],
  agent: { expose: true, surfaces: ['staff.support'], description: 'The scan takes a few seconds (the call waits up to about 25 s in all, then answers complete: false). ops.upstream_refused means the RMS token lacks the scope: do not retry. A printer or terminal the location names that is not in the answer is not on the network the router sees.' },
});

// ---- Commander's Router panel and node checks (lane CM; coordinator-nodded contract note 2026-10-16) -------------------
// The same reads and actions on Commander's staff surface: Commander's token never holds `staff.support.read` (which would
// open the Broker's /mcp/support), so these twins take Commander's own permissions and run the very same server code as the
// diagnostics.* operations above (one Teltonika client, one copy of the rules). Reads are every staff role's
// (`fleet.read`); a node check is `fleet.command.basic`; a reboot is `fleet.command.router` and high risk, behind a
// step-up within five minutes or, on staging's lab mode, the lab policy for an allow-listed lab router.
/** The location's account: Commander reads one location as staff in its account (the tenant its commands there get). */
const StaffAccount = z.uuid();

const STAFF_ROUTER = { permission: 'fleet.read', availability: 'cloud', cloudReason: ROUTER_CLOUD, scope: 'platform', errors: [...routerErrors], agent: { expose: false } } as const;

export const StaffRouterStatus = defineQuery({
  name: 'fleet.staff.router.status',
  summary: "One location's Teltonika routers as RMS reports them, for Radish staff (Commander's Router panel): online, signal, connection, operator, last contact.",
  input: z.object({ accountId: StaffAccount, locationId: LocationId }),
  output: z.object({ routers: z.array(OpsRouterStatus) }),
  ...STAFF_ROUTER,
  errors: [OpsLocationNotFound, OpsNotConfigured, OpsUpstreamFailed],
});

export const StaffRouterSignalHistory = defineQuery({
  name: 'fleet.staff.router.signalHistory',
  summary: "One location's router's mobile signal over the last hours, as RMS recorded it, for Radish staff (Commander's Router panel).",
  input: RouterSignalHistory.input.extend({ accountId: StaffAccount }),
  output: z.object({ rmsDeviceId: z.string(), points: z.array(RouterSignalPoint) }),
  ...STAFF_ROUTER,
});

export const StaffRouterDataUsage = defineQuery({
  name: 'fleet.staff.router.dataUsage',
  summary: "One location's router's mobile data per day over the last one to seven days, as RMS recorded it, for Radish staff (Commander's Router panel).",
  input: RouterDataUsage.input.extend({ accountId: StaffAccount }),
  output: RouterDataUsage.output,
  ...STAFF_ROUTER,
});

export const StaffRouterClients = defineQuery({
  name: 'fleet.staff.router.clients',
  summary: "What is plugged into one location's own router and its Wi-Fi networks, as RMS scans it now, for Radish staff (Commander's Router panel): the same answer as diagnostics.router.clients.",
  input: RouterClients.input.extend({ accountId: StaffAccount }),
  output: RouterClients.output,
  ...STAFF_ROUTER,
  errors: [...routerErrors, OpsUpstreamRefused],
});

export const StaffRebootRouter = defineCommand({
  name: 'fleet.staff.router.reboot',
  summary: "Reboot one location's own Teltonika router through RMS from Commander. High risk: needs a recent step-up (or, on staging's lab mode, a lab router); the router is offline for a few minutes.",
  input: z.object({
    /** The location's account: Radish staff act in it from Commander (`staffAnyLocation`). */
    accountId: z.uuid(), locationId: LocationId, rmsDeviceId: RmsDeviceId,
  }),
  output: RebootRouter.output,
  permission: 'fleet.command.router',
  risk: 'high',
  availability: 'cloud',
  origin: 'any',
  cloudReason: ROUTER_CLOUD,
  emits: [],
  errors: [...routerErrors, StepUpRequired],
  dryRun: true,
  agent: { expose: false },
  staffAnyLocation: true,
});

export const StaffRunNodeCheck = defineCommand({
  name: 'fleet.staff.node.runCheck',
  summary: "Ask one location's node to run a declared check from Commander (ping, disk, clock, a printer or card terminal port, the TLS line to the cloud); the node answers within a beacon or two.",
  input: z.object({
    /** The location's account: Radish staff act in it from Commander (`staffAnyLocation`). */
    accountId: z.uuid(), locationId: LocationId, requestId: z.uuid(), check: StaffNodeCheck,
  }),
  output: RunNodeDiagnostic.output,
  permission: 'fleet.command.basic',
  risk: 'low',
  availability: 'cloud',
  origin: 'any',
  cloudReason: DIAGNOSTICS_CLOUD,
  emits: [],
  errors: [DiagnosticsNoNode, DiagnosticsTooManyPending],
  dryRun: false,
  agent: { expose: false },
  staffAnyLocation: true,
});

export const StaffNodeCheckResult = defineQuery({
  name: 'fleet.staff.node.checkResult',
  summary: "A node check's state and, once the node answered, what it printed and its exit code, for Radish staff (Commander shows the text as plain text).",
  input: NodeDiagnosticResult.input.extend({ accountId: StaffAccount }),
  output: NodeDiagnosticResult.output,
  permission: 'fleet.read',
  availability: 'cloud',
  cloudReason: DIAGNOSTICS_CLOUD,
  scope: 'platform',
  errors: [DiagnosticsRequestNotFound],
  agent: { expose: false },
});

// ---- diagnostics.device.* (lane Q, coordinator-nodded 2026-10-17): a register's device commands for the agent -------
// Commander's own fleet.sendDeviceCommand is medium risk and runs at once; through the Broker an agent's restart must
// be a card the support person answers, so the agent gets its own twins on the same server code (one row per device in
// fleet.device_commands, the same reader gate, expiry and once-only run): a restart is high risk (the Broker holds the
// exact call for a person's approval, and it is never on the lab policy's list), fresh details are low risk (read-only
// allow-listed facts), and the status reads only that location's commands.
const DEVICE_CLOUD = "Commander's device commands are stored in the cloud; a store's node takes its units' commands from there.";

export const RestartDevices = defineCommand({
  name: 'diagnostics.device.restart',
  summary: "Restart the register app, or send it the latest menu & settings, on up to five of one location's registers. High risk: the support person approves exactly this call first. Agents only (through the Broker; a plain call is refused). A restart waits while a payment is being taken, a dialog is open, a Send is going out, the card terminal's parameters are downloading or the cart has unsent changes; a check that is part paid stays saved.",
  input: z.object({
    locationId: LocationId,
    deviceIds: z.array(DeviceId).min(1).max(5),
    command: z.enum(['reloadApp', 'resync']),
    /** The support case this is done in (its location's): the case lists it. */
    caseId: z.uuid().optional(),
  }),
  output: z.object({ commands: z.array(z.object({ deviceId: DeviceId, commandId: z.uuid(), expiresAt: IsoInstant })) }),
  permission: 'fleet.command.basic',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: DEVICE_CLOUD,
  emits: [],
  errors: [DeviceNotFound, SupportCaseNotFound, SupportCaseClosed],
  dryRun: true,
  agent: { expose: true, surfaces: ['staff.support'], description: 'reloadApp restarts the register app; resync sends it the latest menu & settings. Each register runs it once on its next check-in, within five minutes or never. Then read diagnostics.device.status with the command ids.' },
});

export const CollectDeviceInfo = defineCommand({
  name: 'diagnostics.device.collectInfo',
  summary: "Ask one register for fresh details: its app version, Android version, sales waiting to upload, whether it reaches the store's Hub, and its last sync. Changes nothing on the register.",
  input: z.object({ locationId: LocationId, deviceId: DeviceId }),
  output: z.object({ commands: z.array(z.object({ deviceId: DeviceId, commandId: z.uuid(), expiresAt: IsoInstant })) }),
  permission: 'fleet.command.basic',
  risk: 'low',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: DEVICE_CLOUD,
  emits: [],
  errors: [DeviceNotFound],
  dryRun: false,
  agent: { expose: true, surfaces: ['staff.support'], description: 'The register answers on its next check-in (seconds when it is connected): read diagnostics.device.status with the command id.' },
});

export const DeviceCommandsAtLocation = defineQuery({
  name: 'diagnostics.device.status',
  summary: "Where some of one location's device commands stand at their registers (waiting, delivered, acknowledged, completed, failed or expired), with fresh details' answers. Another location's commands are never shown.",
  input: z.object({ locationId: LocationId, commandIds: z.array(z.uuid()).min(1).max(20) }),
  output: z.object({ commands: z.array(DeviceCommandView) }),
  permission: 'fleet.read',
  availability: 'cloud',
  cloudReason: DEVICE_CLOUD,
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['staff.support'] },
});

export const diagnosticsCommands = [RunNodeDiagnostic, RebootRouter, StaffRebootRouter, StaffRunNodeCheck, RestartDevices, CollectDeviceInfo] as const;
export const diagnosticsQueries = [DeviceCommandsAtLocation, NodeDiagnosticResult, RouterSignalHistory, RouterDataUsage, RouterClients, StaffRouterStatus, StaffRouterSignalHistory, StaffRouterDataUsage, StaffRouterClients, StaffNodeCheckResult] as const;
